import { useState } from "react";
import { User, Plus, Trash2, Pencil, Search, ToggleLeft, ToggleRight, ChevronLeft, ChevronRight } from "lucide-react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { PlayerSelect, type PlayerOption } from "@/components/PlayerSelect";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { API_ENDPOINTS } from "@/api/endpoints";
import { toast } from "react-toastify";
import { cn } from "@/lib/utils";

const PAGE_SIZE = 15;

const LEAGUES = ["premier", "laliga", "seriea", "bundesliga", "overall"] as const;
type League = typeof LEAGUES[number];

const LEAGUE_LABELS: Record<League, string> = {
  premier: "Premier League",
  laliga: "La Liga",
  seriea: "Serie A",
  bundesliga: "Bundesliga",
  overall: "Overall / Mixed",
};

interface WhoAmIQuestion {
  id: number;
  league: string;
  imageUrl: string | null;
  isActive: boolean;
  order: number;
  player: {
    id: number;
    name: string;
    photo: string | null;
    position: string;
    age: number | null;
    jerseyNumber: number | null;
    nation: { name: string; code: string } | null;
    club: { name: string } | null;
  };
}

const emptyForm = {
  league: "premier" as League,
  playerId: 0,
  playerName: "",
  imageUrl: "",
};
type FormState = typeof emptyForm;

export default function WhoAmIPage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<WhoAmIQuestion | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<{ playerId?: string }>({});
  const [activeTab, setActiveTab] = useState<League>("premier");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery<WhoAmIQuestion[]>({
    queryKey: ["quiz", "who-am-i"],
    queryFn: async () => {
      const r = (await apiClient.get(API_ENDPOINTS.QUIZ.WHO_AM_I_LIST)) as
        | WhoAmIQuestion[]
        | null;
      return r ?? [];
    },
  });
  const questions: WhoAmIQuestion[] = data ?? [];

  const invalidate = () => qc.invalidateQueries({ queryKey: ["quiz", "who-am-i"] });

  const { mutate: create, isPending: isCreating } = useMutation({
    mutationFn: (d: object) => apiClient.post(API_ENDPOINTS.QUIZ.WHO_AM_I_CREATE, d),
    onSuccess: () => { toast.success("Question created"); invalidate(); closeDialog(); },
    onError: (e: any) => toast.error(e.response?.data?.message || "Failed"),
  });

  const { mutate: update, isPending: isUpdating } = useMutation({
    mutationFn: ({ id, d }: { id: number; d: object }) =>
      apiClient.patch(API_ENDPOINTS.QUIZ.WHO_AM_I_UPDATE(id), d),
    onSuccess: () => { toast.success("Updated"); invalidate(); closeDialog(); },
    onError: (e: any) => toast.error(e.response?.data?.message || "Failed"),
  });

  const { mutate: remove } = useMutation({
    mutationFn: (id: number) => apiClient.delete(API_ENDPOINTS.QUIZ.WHO_AM_I_DELETE(id)),
    onSuccess: () => { toast.success("Deleted"); invalidate(); },
  });

  const { mutate: toggle } = useMutation({
    mutationFn: (id: number) =>
      apiClient.patch(API_ENDPOINTS.QUIZ.WHO_AM_I_TOGGLE(id)),
    onSuccess: () => invalidate(),
    onError: () => toast.error("Toggle failed"),
  });

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, league: activeTab });
    setOpen(true);
  };
  const openEdit = (q: WhoAmIQuestion) => {
    setEditing(q);
    setForm({
      league: q.league as League,
      playerId: q.player.id,
      playerName: q.player.name,
      imageUrl: q.imageUrl ?? q.player.photo ?? "",
    });
    setOpen(true);
  };
  const closeDialog = () => { setOpen(false); setEditing(null); setErrors({}); };

  const handlePlayerChange = (p: PlayerOption | null) => {
    setForm((f) => ({
      ...f,
      playerId: p?.id ?? 0,
      playerName: p?.name ?? "",
      imageUrl: p?.imageUrl ?? "",
    }));
    if (p) setErrors((e) => ({ ...e, playerId: undefined }));
  };

  const handleSave = () => {
    if (!form.playerId) {
      setErrors({ playerId: "Please select a player" });
      return;
    }
    if (!editing) {
      const dup = questions.find((q) => q.player.id === form.playerId && q.league === form.league);
      if (dup) { setErrors({ playerId: `${form.playerName} already exists in this league` }); return; }
    }
    setErrors({});
    const payload = {
      league: form.league,
      playerId: form.playerId,
      ...(form.imageUrl ? { imageUrl: form.imageUrl } : {}),
    };
    if (editing) update({ id: editing.id, d: payload });
    else create(payload);
  };

  // Per-tab counts for tab badges
  const countByLeague = (league: League) =>
    questions.filter((q) => q.league === league).length;

  // Filtered list for the active tab
  const tabQuestions = questions
    .filter((q) => q.league === activeTab)
    .filter((q) => {
      if (!search.trim()) return true;
      const s = search.toLowerCase();
      return (
        q.player.name.toLowerCase().includes(s) ||
        q.player.club?.name?.toLowerCase().includes(s) ||
        q.player.nation?.code?.toLowerCase().includes(s)
      );
    });

  const activeCount = tabQuestions.filter((q) => q.isActive).length;

  const totalPages = Math.max(1, Math.ceil(tabQuestions.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = tabQuestions.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const handleTabChange = (l: League) => { setActiveTab(l); setSearch(""); setPage(1); };
  const handleSearch = (v: string) => { setSearch(v); setPage(1); };

  return (
    <Layout>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <User className="h-8 w-8 text-violet-500" />
            Who Am I?
          </h1>
          <p className="text-gray-500 mt-1">
            {questions.length} questions total · {questions.filter((q) => q.isActive).length} active
          </p>
        </div>
        <Button onClick={openCreate} className="bg-violet-600 hover:bg-violet-700 text-white">
          <Plus className="h-4 w-4 mr-2" />
          New Question
        </Button>
      </div>

      {/* League tabs */}
      <div className="flex gap-1 border-b border-gray-200 mb-4">
        {LEAGUES.map((l) => {
          const count = countByLeague(l);
          return (
            <button
              key={l}
              onClick={() => handleTabChange(l)}
              className={cn(
                "flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium rounded-t-lg border-b-2 transition-colors",
                activeTab === l
                  ? "border-violet-600 text-violet-600 bg-violet-50"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50",
              )}
            >
              {LEAGUE_LABELS[l]}
              <span
                className={cn(
                  "text-xs px-1.5 py-0.5 rounded-full font-semibold",
                  activeTab === l ? "bg-violet-100 text-violet-700" : "bg-gray-100 text-gray-500",
                )}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Search + stats row */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search by name, club, nation…"
            className="pl-8 h-9 text-sm"
          />
        </div>
        <span className="text-xs text-gray-400">
          {tabQuestions.length} shown · {activeCount} active
          {totalPages > 1 && ` · page ${safePage}/${totalPages}`}
        </span>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-violet-500" />
        </div>
      ) : tabQuestions.length === 0 ? (
        <div className="text-center py-20 text-gray-400">
          <User className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p className="font-medium">
            {search ? "No matches for your search" : "No questions in this league yet"}
          </p>
        </div>
      ) : (
        <>
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                <th className="text-left px-4 py-2.5 font-medium">Player</th>
                <th className="text-left px-4 py-2.5 font-medium">Nat</th>
                <th className="text-left px-4 py-2.5 font-medium">Club</th>
                <th className="text-left px-4 py-2.5 font-medium">Pos</th>
                <th className="text-left px-4 py-2.5 font-medium">Age</th>
                <th className="text-left px-4 py-2.5 font-medium">Shirt</th>
                <th className="text-left px-4 py-2.5 font-medium">Status</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {paged.map((q) => (
                <tr key={q.id} className={cn("hover:bg-gray-50 transition-colors", !q.isActive && "opacity-50")}>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      {(q.imageUrl ?? q.player.photo) ? (
                        <img
                          src={(q.imageUrl ?? q.player.photo)!}
                          alt=""
                          className="w-7 h-7 rounded-full object-cover shrink-0"
                        />
                      ) : (
                        <div className="w-7 h-7 rounded-full bg-gray-100 shrink-0" />
                      )}
                      <span className="font-medium text-gray-900">{q.player.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-gray-600">{q.player.nation?.code ?? "—"}</td>
                  <td className="px-4 py-2.5 text-gray-600 max-w-[140px] truncate">{q.player.club?.name ?? "—"}</td>
                  <td className="px-4 py-2.5 text-gray-600">{q.player.position}</td>
                  <td className="px-4 py-2.5 text-gray-600">{q.player.age ?? "—"}</td>
                  <td className="px-4 py-2.5 text-gray-600">#{q.player.jerseyNumber ?? "—"}</td>
                  <td className="px-4 py-2.5">
                    <button
                      onClick={() => toggle(q.id)}
                      className={cn(
                        "flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full transition-colors",
                        q.isActive
                          ? "bg-green-100 text-green-700 hover:bg-green-200"
                          : "bg-gray-100 text-gray-500 hover:bg-gray-200",
                      )}
                    >
                      {q.isActive
                        ? <><ToggleRight className="h-3.5 w-3.5" /> Active</>
                        : <><ToggleLeft className="h-3.5 w-3.5" /> Inactive</>
                      }
                    </button>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-1 justify-end">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(q)}>
                        <Pencil className="h-3.5 w-3.5 text-gray-400" />
                      </Button>
                      <Button
                        variant="ghost" size="sm" className="h-7 w-7 p-0"
                        onClick={() => { if (confirm(`Delete ${q.player.name}?`)) remove(q.id); }}
                      >
                        <Trash2 className="h-3.5 w-3.5 text-red-400" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 text-sm text-gray-500">
              <span>
                {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, tabQuestions.length)} of {tabQuestions.length}
              </span>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline" size="sm" className="h-8 w-8 p-0"
                  disabled={safePage === 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((n) => n === 1 || n === totalPages || Math.abs(n - safePage) <= 1)
                  .reduce<(number | "…")[]>((acc, n, idx, arr) => {
                    if (idx > 0 && n - (arr[idx - 1] as number) > 1) acc.push("…");
                    acc.push(n);
                    return acc;
                  }, [])
                  .map((n, i) =>
                    n === "…" ? (
                      <span key={`ellipsis-${i}`} className="px-1">…</span>
                    ) : (
                      <Button
                        key={n}
                        variant={n === safePage ? "default" : "outline"}
                        size="sm"
                        className={cn("h-8 w-8 p-0", n === safePage && "bg-violet-600 hover:bg-violet-700 text-white")}
                        onClick={() => setPage(n as number)}
                      >
                        {n}
                      </Button>
                    )
                  )}
                <Button
                  variant="outline" size="sm" className="h-8 w-8 p-0"
                  disabled={safePage === totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Question" : "New Who Am I Question"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="mb-1 block text-sm">League</Label>
              <Select value={form.league} onValueChange={(v) => setForm((f) => ({ ...f, league: v as League }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LEAGUES.map((l) => (
                    <SelectItem key={l} value={l}>{LEAGUE_LABELS[l]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1 block text-sm">
                Player (Answer) <span className="text-red-500">*</span>
              </Label>
              <PlayerSelect
                value={form.playerName ? { name: form.playerName, imageUrl: form.imageUrl } : null}
                onChange={handlePlayerChange}
              />
              {errors.playerId ? (
                <p className="text-xs text-red-500 mt-1 font-medium">{errors.playerId}</p>
              ) : (
                <p className="text-xs text-gray-400 mt-1">All player details are pulled from the Players table.</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>Cancel</Button>
            <Button
              onClick={handleSave}
              disabled={isCreating || isUpdating}
              className="bg-violet-600 hover:bg-violet-700 text-white"
            >
              {isCreating || isUpdating ? "Saving…" : editing ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
